import Card from '@/components/card';
import FormSection from '@/components/form/form-section';
import PageLink from '@/components/page-link';
import Text from '@/components/text';
import { ClassifierSubType } from '@/lib/models/classifier';
import {
  ClassifierControlsSettings,
  RegexPatternConfig,
  getClassifierControlActionOptions,
} from '@/lib/models/policy-model/classifier-controls';
import { ControlActionValue } from '@/lib/models/policy-model/controls';
import { FC, useCallback, useMemo } from 'react';
import {
  ClassifierControlListProps,
  getClassifierItemOptions,
  updateClassifierAction,
} from './helpers';
import Item from './item';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const EntityControlList: FC<ClassifierControlListProps> = ({
  formHandle,
  readonly,
  defaultPolicy,
  withNotSet = true,
  classifiers,
  refetch,
}) => {
  const { values, setFieldValue } = formHandle;
  const { regexPatterns = new RegexPatternConfig() } = values;
  const { entities = [] } = regexPatterns;
  const defaultRegexPatterns = defaultPolicy?.regexPatterns;

  const entityClassifiers = useMemo(
    () => classifiers.filter(({ subtype }) => subtype === ClassifierSubType.Regex),
    [classifiers]
  );
  const hasClassifiers = !!entityClassifiers.length;

  const handleChange = useCallback(
    (classifierId: string, action: ControlActionValue) => {
      const updated = updateClassifierAction(entities, classifierId, action);
      setFieldValue('regexPatterns.entities', updated);
    },
    [entities, setFieldValue]
  );

  const actionOptionsMap = useMemo(
    () => getClassifierControlActionOptions(entityClassifiers),
    [entityClassifiers]
  );

  const classifierSettings = useMemo(
    () => ClassifierControlsSettings(entityClassifiers),
    [entityClassifiers]
  );

  return (
    <FormSection title="Custom Entity Controls" scrollId="custom-entity-controls">
      <FormSectionSubtitle>
        Choose what action is taken when a request matches a custom entity. Head over to{' '}
        <PageLink routeKey="classifiers" /> to create or manage custom entities.
      </FormSectionSubtitle>
      <Card>
        {!hasClassifiers ? (
          <Text italic>No custom entities found.</Text>
        ) : (
          classifierSettings.map(({ classifierId, label, description, enabled }) => {
            const entity = entities.find(({ classifierId: id }) => id === classifierId);
            const { action: defaultPolicyAction } =
              (defaultRegexPatterns?.entities ?? []).find(
                ({ classifierId: id }) => id === classifierId
              ) || {};

            const options = getClassifierItemOptions(
              actionOptionsMap,
              classifierId,
              defaultPolicyAction
            );

            return (
              <Item
                key={`entity-${classifierId}`}
                classifierId={classifierId}
                label={label}
                description={description}
                enabled={!!enabled}
                readonly={readonly}
                name={`regexPatterns.${classifierId}`}
                options={options}
                value={entity?.action || ''}
                withNotSet={withNotSet}
                refetch={refetch}
                onChange={(value: string) =>
                  handleChange(classifierId, value as ControlActionValue)
                }
              />
            );
          })
        )}
      </Card>
    </FormSection>
  );
};

export default EntityControlList;
